import React, { useMemo } from "react";
import { FaMapMarkerAlt, FaTrain, FaCubes, FaMapSigns } from "react-icons/fa";
import AnimatedDisplay from "../../CHARTS/AnimatedDisplay";

const CASIndentKpi = ({ data }) => {
  const computed = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];

    let totalRecords = safeData.length;
    let totalWagons = 0;

    const wagonCounts = {};
    const sourceCounts = {};
    const destCounts = {};

    safeData.forEach((row) => {
      totalWagons += Number(row.wagons) || 0;

      // Wagon Types
      if (row.wagon_type && row.wagon_type !== "-") {
        wagonCounts[row.wagon_type] = (wagonCounts[row.wagon_type] || 0) + 1;
      }

      // Siding
      if (row.source_station && row.source_station !== "-") {
        row.source_station.split(" + ").forEach((s) => {
          const cleanS = s.trim();
          sourceCounts[cleanS] = (sourceCounts[cleanS] || 0) + 1;
        });
      }

      // Destination
      if (row.destination_station && row.destination_station !== "-") {
        row.destination_station.split(" + ").forEach((d) => {
          const cleanD = d.trim();
          destCounts[cleanD] = (destCounts[cleanD] || 0) + 1;
        });
      }
    });

    const sortCounts = (dict) =>
      Object.entries(dict)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

    return {
      totalRecords,
      totalWagons,
      topWagons: sortCounts(wagonCounts),
      topSources: sortCounts(sourceCounts),
      topDest: sortCounts(destCounts),
    };
  }, [data]);

  const formatTop3 = (arr, fallbackLabel) => {
    if (arr.length === 0) return `No ${fallbackLabel}`;
    return arr
      .slice(0, 3)
      .map((r, i) => `${i + 1}) ${r.name} – ${r.count}`)
      .join("\n");
  };

  // --- CARD CONFIGURATION ---
  const cards = [
    {
      label: "Total Demand",
      value: computed.totalRecords,
      sub: `Rake Volume: ${computed.totalWagons}`,
      icon: <FaTrain />,
      color: "#d686aeff",
    },
    {
      label: "Top Wagon Types",
      value: computed.topWagons[0]?.name || "-",
      sub: formatTop3(computed.topWagons, "Wagons"),
      icon: <FaCubes />,
      color: "#357fc0ff",
    },
    {
      label: "Busiest Siding",
      value: computed.topSources[0]?.name || "-",
      sub: formatTop3(computed.topSources, "Terminals"),
      icon: <FaMapMarkerAlt />,
      color: "#e45c5cff",
    },
    {
      label: "Top Destination",
      value: computed.topDest[0]?.name || "-",
      sub: formatTop3(computed.topDest, "Destinations"),
      icon: <FaMapSigns />,
      color: "#53b2c5ff",
    },
  ];

  const gridStyle = {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
    gap: "18px",
    overflowX: "auto",
    padding: "6px 0",
  };

  return (
    <div className="newUiBody ps-2 pe-2 mt-2 p-0">
      <div style={gridStyle}>
        {cards.map((card, idx) => (
          <div
            className="newUiBody me-1"
            key={idx}
            style={{
              borderRadius: 12,
              display: "flex",
              gap: 12,
              alignItems: "center",
              fontWeight: "bold",
              minHeight: 84,
              minWidth: 220,
              transition: "transform 160ms ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "translateY(-4px)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: card.color,
                color: "#fff",
                fontSize: 20,
              }}
            >
              {card.icon}
            </div>
            {/* card heading */}
            <div style={{ flex: 1 }}>
              <div
                style={{ fontSize: 16, color: "#6c757d", textAlign: "left" }}
              >
                {card.label}
              </div>
              {/* card value */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    height: "100%",
                    fontSize: 20,
                    color: "#212529",
                    textAlign: "left",
                  }}
                >
                  <AnimatedDisplay value={card.value} />
                </div>
                {/* card results top 3 */}
                {card.sub && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#6c757d",
                      whiteSpace: "pre-line",
                      marginTop: 6,
                      lineHeight: 1.3,
                      textAlign: "left",
                    }}
                  >
                    {card.sub}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CASIndentKpi;
